// Librairies
import { computed, effect, inject, Injectable, signal, Signal, untracked, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs';

// Services
import { CompanyService } from 'src/app/services/api/companies/company.service';
import { DashboardService } from 'src/app/services/api/dashboards/dashboard.service';
import { CompanyStore } from 'src/app/services/stores/company.store';

// Interfaces
import { CompanyDetail, NamedRef, Tenant } from 'src/app/interfaces/api/company';
import { CompanyDashboard, SYNTHESIS_THEMES, SynthesisTheme, WidgetEditorScope } from 'src/app/interfaces/api/dashboard';

/**
 * État partagé des pages de synthèse entreprise : la compagnie courante et ses tenants, le dashboard de widgets
 * du thème ouvert (Prévention, Production, Sécurité, Environnement) et les projets retirés de la page.
 */
@Injectable({ providedIn: 'root' })
export class SynthesisStore {
  // Services
  private readonly companyService: CompanyService = inject(CompanyService);
  private readonly companyStore: CompanyStore = inject(CompanyStore);
  private readonly dashboardService: DashboardService = inject(DashboardService);
  private readonly router: Router = inject(Router);

  // Signals
  /** Projets du tenant retirés de la page, pour une lecture rapide : jamais enregistré, remis à zéro à chaque tenant. */
  private readonly excludedState = signal<string[]>([]);

  private readonly companyState = signal<CompanyDetail | null>(null);
  private readonly loadingState = signal<boolean>(true);
  private readonly failedState = signal<boolean>(false);

  /** Compagnie courante (pastille du header) et ses tenants. */
  readonly company: Signal<CompanyDetail | null> = this.companyState.asReadonly();
  readonly loading: Signal<boolean> = this.loadingState.asReadonly();
  readonly failed: Signal<boolean> = this.failedState.asReadonly();
  readonly tenants: Signal<Tenant<NamedRef>[]> = computed(() => this.companyState()?.tenants ?? []);

  /**
   * Lu dans l'URL et non dans la route : la RouteReuseStrategy garde les pages
   * détachées en vie, leurs paramètres ne suivent donc pas la navigation.
   */
  private readonly url: Signal<string> = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects)
    ),
    { initialValue: this.router.url }
  );

  readonly tenantId: Signal<string | null> = computed(() => /^\/synthesis\/([^/?#]+)/.exec(this.url())?.[1] ?? null);
  /** Onglet ouvert, conservé quand on change de tenant. */
  readonly theme: Signal<string> = computed(() => /^\/synthesis\/[^/?#]+\/([^/?#]+)/.exec(this.url())?.[1] ?? 'prevention');
  readonly tenant: Signal<Tenant<NamedRef> | null> = computed(() =>
    this.tenants().find(tenant => tenant.id === this.tenantId()) ?? null
  );

  /**
   * Dashboards de synthèse déjà chargés, par compagnie et thème : un onglet rouvert reprend le sien
   * (même objet, donc mêmes widgets déjà chargés), sans requête ; « ajouter » et « recharger » le remplacent.
   */
  private readonly dashboardsState = signal<Record<string, CompanyDashboard>>({});
  private readonly dashboardKeyState = signal<string | null>(null);
  private readonly dashboardLoadingState = signal<boolean>(false);
  private readonly dashboardFailedState = signal<boolean>(false);

  /** Dashboard de synthèse du thème ouvert, commun à tous les tenants de la compagnie. */
  readonly dashboard: Signal<CompanyDashboard | null> = computed(() => {
    const key = this.dashboardKeyState();
    return key ? this.dashboardsState()[key] ?? null : null;
  });
  readonly dashboardLoading: Signal<boolean> = this.dashboardLoadingState.asReadonly();
  readonly dashboardFailed: Signal<boolean> = this.dashboardFailedState.asReadonly();
  readonly canEdit: Signal<boolean> = computed(() => !!this.dashboard()?.canEdit);
  /** Incrémenté pour relancer les requêtes des widgets affichés (bouton rafraîchir). */
  readonly refreshSignal: WritableSignal<number> = signal<number>(0);

  private readonly companyId: Signal<string | null> = computed(() => this.companyState()?.id ?? null);

  /** Contexte de l'éditeur de widgets pour la page ouverte, `null` hors d'une page de thème. */
  readonly editorScope: Signal<WidgetEditorScope | null> = computed(() => {
    const companyId = this.companyId();
    const tenantId = this.tenant()?.id;
    const theme = this.theme() as SynthesisTheme;
    if (!companyId || !tenantId || !SYNTHESIS_THEMES.includes(theme)) return null;
    return { companyId, tenantId, theme };
  });

  private readonly onPicker: Signal<boolean> = computed(() => this.url().split(/[?#]/)[0] === '/synthesis');
  private loadedId: string | null | undefined;

  /** Projets retirés, transmis aux widgets du dashboard de synthèse. */
  readonly excludedProjects: Signal<string[]> = this.excludedState.asReadonly();
  readonly isFiltered: Signal<boolean> = computed(() => this.excludedState().length > 0);

  constructor() {
    // Rechargée à chaque retour sur le choix du tenant : ils ont pu changer sur /company.
    effect(() => {
      const id = this.companyStore.ready() ? this.companyStore.current()?.id ?? null : undefined;
      const onPicker = this.onPicker();
      untracked(() => {
        if (id !== undefined && (onPicker || id !== this.loadedId)) this.load(id);
      });
    });

    // Le dashboard du thème ouvert : celui déjà chargé, sinon chargé (à chaque changement de compagnie ou de thème).
    effect(() => {
      const companyId = this.companyId();
      const theme = this.theme() as SynthesisTheme;
      const ready = !!this.tenant() && SYNTHESIS_THEMES.includes(theme);
      untracked(() => ready && companyId ? this.showDashboard(companyId, theme) : this.clearDashboard());
    });

    // Compagnie affichée modifiée dans l'onglet (tenants, projets) : rechargée.
    effect(() => {
      const change = this.companyService.changed();
      untracked(() => {
        if (change && change.id === this.companyState()?.id) this.load(change.id);
      });
    });

    // Toute la sélection à chaque tenant (son dashboard est recréé : pas de rafraîchissement).
    effect(() => {
      this.tenantId();
      untracked(() => this.excludedState.set([]));
    });

    // Tenant inconnu, hors de la compagnie courante, ou compagnie changée : retour au choix.
    effect(() => {
      if (this.tenantId() && !this.loadingState() && !this.tenant())
        untracked(() => this.router.navigate(['/synthesis']));
    });
  }

  /** Dashboard renvoyé par l'API après un ajout ou un retrait de widget. */
  setDashboard(dashboard: CompanyDashboard): void {
    const key = this.dashboardKeyState();
    if (key) this.dashboardsState.update(dashboards => ({ ...dashboards, [key]: dashboard }));
  }

  reloadDashboard(): void {
    const companyId = this.companyId();
    if (companyId) this.loadDashboard(companyId, this.theme() as SynthesisTheme);
  }

  refreshWidgets(): void {
    this.refreshSignal.update(value => value + 1);
  }

  reload(): void {
    this.load(this.companyStore.current()?.id ?? null);
  }

  private showDashboard(companyId: string, theme: SynthesisTheme): void {
    const key = `${companyId}/${theme}`;
    this.dashboardKeyState.set(key);
    this.dashboardFailedState.set(false);
    if (!this.dashboardsState()[key]) this.loadDashboard(companyId, theme);
    else this.dashboardLoadingState.set(false);
  }

  private loadDashboard(companyId: string, theme: SynthesisTheme): void {
    const key = `${companyId}/${theme}`;
    this.dashboardKeyState.set(key);
    this.dashboardFailedState.set(false);
    this.dashboardLoadingState.set(true);

    this.dashboardService.getCompanyDashboard(companyId, theme).subscribe({
      next: dashboard => {
        this.dashboardsState.update(dashboards => ({ ...dashboards, [key]: dashboard }));
        if (this.dashboardKeyState() === key) this.dashboardLoadingState.set(false);
      },
      error: () => {
        if (this.dashboardKeyState() !== key) return;
        this.dashboardFailedState.set(true);
        this.dashboardLoadingState.set(false);
      }
    });
  }

  private clearDashboard(): void {
    this.dashboardKeyState.set(null);
    this.dashboardFailedState.set(false);
    this.dashboardLoadingState.set(false);
  }

  private load(id: string | null): void {
    this.loadedId = id;
    this.failedState.set(false);
    if (!id) {
      this.companyState.set(null);
      this.loadingState.set(false);
      return;
    }

    // La compagnie affichée reste en place pendant le rechargement : pas de clignotement.
    this.loadingState.set(true);
    this.companyService.getCompanyTenants(id).subscribe({
      next: company => {
        if (this.loadedId !== id) return;
        this.companyState.set(company);
        this.loadingState.set(false);
      },
      error: () => {
        if (this.loadedId !== id) return;
        this.companyState.set(null);
        this.failedState.set(true);
        this.loadingState.set(false);
      }
    });
  }

  /** Retire ou remet un projet du tenant, puis relance les widgets. */
  toggleProject(id: string): void {
    const excluded = this.excludedState();
    const count = this.tenant()?.projects.length ?? 0;

    // Jamais le dernier projet : les synthèses n'auraient plus de sens.
    if (!excluded.includes(id) && excluded.length >= count - 1) return;

    this.excludedState.set(excluded.includes(id) ? excluded.filter(e => e !== id) : [...excluded, id]);
    this.refreshWidgets();
  }

  isProjectSelected(id: string): boolean {
    return !this.excludedState().includes(id);
  }

  resetProjects(): void {
    if (!this.isFiltered()) return;
    this.excludedState.set([]);
    this.refreshWidgets();
  }
}
